<script setup lang="ts">
import { computed } from "vue";

interface Props {
  tag?: "button" | "a";
  color?: "primary" | "error" | "accent";
  size?: "s" | "l" | "xl";
  variant?: "light" | "dark";
}

const props = withDefaults(defineProps<Props>(), { tag: "button", color: "primary", size: "l", variant: "light" });

const COMPONENT_CLASS = "le-icon-button";

const componentClasses = computed(() => [
  COMPONENT_CLASS,
  `${COMPONENT_CLASS}--size-${props.size}`,
  `${COMPONENT_CLASS}--color-${props.color}-${props.variant}`
]);
</script>

<template>
  <Component :is="tag" :type="tag === 'button' ? 'button' : undefined" :class="componentClasses">
    <span class="le-icon-button__content">
      <slot />
    </span>
  </Component>
</template>

<style scoped lang="less">
@import "@dsk/styles/all";

@color-map: {
  @primary-light: {
    background: @colors[@primary][100];
    color: @colors[@primary][700];
    hover-background: @colors[@primary][200];
    focus: green;
  };
  @primary-dark: {
    background: @colors[@primary][200];
    color: @colors[@primary][700];
    hover-background: @colors[@primary][300];
    focus: green;
  };
  @error-light: {
    background: @colors[@error][100];
    color: @colors[@error][500];
    hover-background: @colors[@error][200];
    focus: red;
  };
  @error-dark: {
    background: @colors[@error][200];
    color: @colors[@error][500];
    hover-background: @colors[@error][300];
    focus: red;
  };
  @accent-light: {
    background: @colors[@accent][100];
    color: @colors[@accent][900];
    hover-background: @colors[@accent][200];
    focus: yellow;
  };
  @accent-dark: {
    background: @colors[@accent][300];
    color: @colors[@accent][900];
    hover-background: @colors[@accent][400];
    focus: yellow;
  };
};

.le-icon-button {
  display: inline-block;
  padding: 6px;
  transition: all 75ms ease-out;
  border: none;
  border-radius: 50%;

  &--size {
    &-s {
      width: 24px;
      height: 24px;
    }

    &-l {
      width: 28px;
      height: 28px;
    }

    &-xl {
      width: 40px;
      height: 40px;
    }
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  each(@color-map, {
    @color: replace(@key, "@", "");

    &--color-@{color} {
      background: @value[background];
      color: @value[color];

      &:hover:not(:disabled) {
        background: @value[hover-background];
      }

      &.focus-visible {
        .focus-outline(@value[focus]);
      }
    }
  });
}
</style>
